import React, { useMemo, useState } from 'react';
import { Table, Input, Button, Space, Typography } from 'antd';

const { Title, Text } = Typography;

export function Modem(props) {
    const { device, isAdmin } = props;
    const canEdit = !!isAdmin;

    const initialPPPOEAuth = useMemo(() => {
        if (device && device.getPPPOEAuth) {
            return device.getPPPOEAuth();
        }
        return {
            username: '02583466679',
            password: '889977',
        };
    }, [device]);

    const initialDNS = useMemo(() => {
        if (device && device.dnsTable && Array.isArray(device.dnsTable)) {
            return device.dnsTable;
        }
        return [];
    }, [device]);

    const [pppoeAuth, setPPPOEAuth] = useState(initialPPPOEAuth);
    const [dnsRecords, setDnsRecords] = useState(initialDNS);
    const [version, setVersion] = useState(0);

    const commitPPPOEAuth = (nextAuth) => {
        if (!canEdit) {
            return;
        }
        if (!nextAuth.username || !nextAuth.password) {
            return;
        }
        setPPPOEAuth(nextAuth);
        if (device && device.updatePPPOEAuth) {
            device.updatePPPOEAuth(nextAuth.username, nextAuth.password);
        }
    };

    const commitDNSRecords = (records) => {
        if (!canEdit) {
            return;
        }
        setDnsRecords(records);
        if (device && device.updateDNSRecords) {
            device.updateDNSRecords(records);
        }
    };

    const handleResetDefaults = () => {
        if (!canEdit) {
            return;
        }

        if (device && device.resetToDefaultConfigs) {
            device.resetToDefaultConfigs();
        }

        const nextAuth = device && device.getPPPOEAuth ? device.getPPPOEAuth() : initialPPPOEAuth;
        const nextDNS = device && device.dnsTable && Array.isArray(device.dnsTable) ? device.dnsTable : initialDNS;

        setPPPOEAuth(nextAuth);
        setDnsRecords(nextDNS);
        setVersion((v) => v + 1);
    };

    const handleAddRow = () => {
        if (!canEdit) {
            return;
        }
        const next = [
            ...dnsRecords,
            { domainName: '', ip: '' },
        ];
        setDnsRecords(next);
        if (device && device.updateDNSRecords) {
            device.updateDNSRecords(next);
        }
    };

    const handleRemoveRow = (index) => {
        if (!canEdit) {
            return;
        }
        const next = dnsRecords.filter((_, idx) => idx !== index);
        setDnsRecords(next);
        if (device && device.updateDNSRecords) {
            device.updateDNSRecords(next);
        }
    };

    const pppoeColumns = [
        {
            title: '账号',
            dataIndex: 'username',
            render: (_, record) => (
                <Input
                    disabled={!canEdit}
                    key={`pppoe-username-${version}`}
                    defaultValue={record.username}
                    onBlur={(e) => commitPPPOEAuth({ ...pppoeAuth, username: e.target.value })}
                />
            ),
        },
        {
            title: '密码',
            dataIndex: 'password',
            render: (_, record) => (
                <Input.Password
                    disabled={!canEdit}
                    defaultValue={record.password}
                    onBlur={(e) => commitPPPOEAuth({ ...pppoeAuth, password: e.target.value })}
                />
            ),
        },
    ];

    const dnsColumns = [
        {
            title: '域名',
            dataIndex: 'domainName',
            render: (value, _, index) => (
                <Input
                    disabled={!canEdit}
                    key={`dns-domain-${index}-${version}`}
                    defaultValue={value}
                    placeholder="例：www.example.com"
                    onBlur={(e) => {
                        const next = dnsRecords.map((item, idx) => idx === index ? { ...item, domainName: e.target.value } : item);
                        commitDNSRecords(next);
                    }}
                />
            ),
        },
        {
            title: 'IP',
            dataIndex: 'ip',
            render: (value, _, index) => (
                <Input
                    disabled={!canEdit}
                    key={`dns-ip-${index}-${version}`}
                    defaultValue={value}
                    placeholder="例：203.0.113.1"
                    onBlur={(e) => {
                        const next = dnsRecords.map((item, idx) => idx === index ? { ...item, ip: e.target.value } : item);
                        commitDNSRecords(next);
                    }}
                />
            ),
        },
    ];

    if (canEdit) {
        dnsColumns.push({
            title: '操作',
            dataIndex: 'action',
            render: (_, __, index) => (
                <Button danger size="small" onClick={() => handleRemoveRow(index)}>
                    删除
                </Button>
            ),
        });
    }

    const pppoeData = [
        { key: 'pppoe', ...pppoeAuth },
    ];

    const dnsData = dnsRecords.map((item, index) => ({
        key: `${index}`,
        ...item,
    }));

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={3} style={{ margin: 0 }}>模拟互联网参数表</Title>
                <Button onClick={handleResetDefaults} disabled={!canEdit}>
                    恢复默认配置
                </Button>
            </div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                    <Space align="center" style={{ marginBottom: 8 }}>
                        <Title level={4} style={{ margin: 0 }}>PPPoE 账号/密码</Title>
                        {!canEdit ? <Text type="secondary">只读</Text> : null}
                    </Space>
                    <Table
                        bordered
                        pagination={false}
                        columns={pppoeColumns}
                        dataSource={pppoeData}
                        size="small"
                    />
                </div>

                <div>
                    <Space align="center" style={{ marginBottom: 8 }}>
                        <Title level={4} style={{ margin: 0 }}>DNS 与 HTTP 服务配置</Title>
                        {!canEdit ? <Text type="secondary">只读</Text> : null}
                        {canEdit ? (
                            <Button type="primary" size="small" onClick={handleAddRow}>
                                新增记录
                            </Button>
                        ) : null}
                    </Space>
                    <Table
                        bordered
                        pagination={false}
                        columns={dnsColumns}
                        dataSource={dnsData}
                        size="small"
                    />
                </div>
            </Space>
        </div>
    )
}